import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import useCart from "store";

import Api from "utils/wApi/index";
import { fDate } from "utils/formatTime";
import { selectButtonStyle } from "utils/uiData";
import { SET_PRICE_CURRENCY } from "utils/function";
import dayjs from "dayjs";
import Cookie from "js-cookie";

import {
	Grid,
	Typography,
	Box,
	Button,
	CircularProgress,
	Stack,
	Radio,
} from "@mui/material";

export default function SelectTime({
	menu,
	time,
	timeOption,
	setTime,
	setTimeOption,
	setCount,
	setMaxCount,
}) {
	const { t } = useTranslation();
	const { language, timezone, cart } = useCart((state) => state);

	const [loading, setLoading] = useState(true);
	const [timelineList, setTimelineList] = useState([]); // 시간대 리스트
	const [activeTimeList, setActiveTimeList] = useState(""); // 선택된 시간:분
	const [timelist, setTimeList] = useState([]); // 선택된 시간대의 시간 리스트

	const timeRef = useRef(null); // 폴링에서 최신 time 참조용

	// ===== CLICK EVENT =====
	// 시간대 클릭 이벤트
	const clickTimeLine = (timeline) => {
		setCount(() => 1);
		setActiveTimeList(() => timeline);

		GET_TIMELIST_API(timeline);
	};

	// ===== CLICK EVENT =====

	// ===== 시간 리스트 조회 =====
	const GET_TIMELIST_API = async (timeline) => {
		// 시간대 api 조회
		const {
			data: { data },
		} = await Api.getTimeline(null, menu.idMenu, {
			params: {
        date: Cookie.get("qs_kiosk_date") ?? dayjs().format("YYYY-MM-DD"),
				timezone: timezone,
				time: timeline,
			},
		});
		setTimeList(() => data[timeline]);
		setTime(data[timeline]?.[0]);
	};

	// ===== 초기 데이터 조회 =====
	const INIT = () => {
		const TIME_LINE = Object.keys(menu?.timeInfo?.timeStocks);
		const optionId =
			menu.timeInfo.timeTypes.length > 0 ? menu?.timeInfo?.timeTypes[0] : null;
		try {
			setTimelineList(TIME_LINE);
			setActiveTimeList(TIME_LINE[0]);
			setMaxCount(TIME_LINE[0]);
			GET_TIMELIST_API(TIME_LINE[0]);
			setTimeOption(optionId);
		} finally {
			setLoading(false);
		}
	};

	// 장바구니 변경 후 시간재고 갱신 (선택 슬롯 유지)
	useEffect(() => {
		if (activeTimeList === "") return;
		setCount(() => 1);

		const refreshStock = async () => {
			try {
				const {
					data: { data },
				} = await Api.getTimeline(null, menu.idMenu, {
					params: {
						date: Cookie.get("qs_kiosk_date") ?? dayjs().format("YYYY-MM-DD"),
						timezone: timezone,
						time: activeTimeList,
					},
				});

				const newTimeList = data[activeTimeList];
				setTimeList(newTimeList);

				// 선택한 시간 슬롯의 재고만 갱신 (선택 유지)
				const currentTime = timeRef.current;
				if (currentTime && newTimeList) {
					const selectedSlot = newTimeList.find(
						(slot) => slot.idMenuTime === currentTime.idMenuTime
					);

					if (!selectedSlot || selectedSlot.stock <= 0) {
						setTime(null);
					} else {
						setTime(selectedSlot);
					}
				}
			} catch (e) {
				// 실패 시 기존 데이터 유지
			}
		};

		refreshStock();
	}, [cart]);

	useEffect(() => {
		INIT();
	}, []);

	// timeRef 동기화 (폴링에서 최신 time 참조)
	useEffect(() => {
		timeRef.current = time;
	}, [time]);

	// 재고 폴링 (5초마다 시간 슬롯 데이터 갱신)
	useEffect(() => {
		if (!activeTimeList) return;

		const POLLING_INTERVAL = 5000;

		const poll = async () => {
			if (document.hidden) return;
			try {
				const {
					data: { data },
				} = await Api.getTimeline(null, menu.idMenu, {
					params: {
						date: Cookie.get("qs_kiosk_date") ?? dayjs().format("YYYY-MM-DD"),
						timezone: timezone,
						time: activeTimeList,
					},
				});

				const newTimeList = data[activeTimeList];
				setTimeList(newTimeList);

				// 선택한 시간 슬롯의 재고 확인
				const currentTime = timeRef.current;
				if (currentTime && newTimeList) {
					const selectedSlot = newTimeList.find(
						(slot) => slot.idMenuTime === currentTime.idMenuTime
					);

					if (!selectedSlot || selectedSlot.stock <= 0) {
						setTime(null); // 품절된 슬롯 선택 해제
					} else {
						setTime(selectedSlot); // 최신 재고 정보로 업데이트
					}
				}
			} catch (e) {
				// 폴링 실패 시 기존 데이터 유지
			}
		};

		const intervalId = setInterval(poll, POLLING_INTERVAL);
		return () => clearInterval(intervalId);
	}, [activeTimeList, menu.idMenu, timezone]);

	return (
		<>
			{loading ? (
				<Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
					<CircularProgress />
				</Box>
			) : (
				<Stack gap={"20px"} mb={"20px"}>
					{/* ===== 종류형 옵션 ===== */}

					{menu.timeInfo.timeTypes.length > 0 ? (
						<>
							<Typography variant="subtitle2" color={"grey.100"}>
								{t("select-option")}
							</Typography>

							<Stack gap={1} mb={2.5}>
								{menu.timeInfo.timeTypes.map((option) => (
									<Stack
										direction={"row"}
										alignItems={"center"}
										gap={1}
										onClick={() => setTimeOption(option)}
									>
										<Radio
											key={option.id}
											checked={timeOption?.id === option?.id}
											value={option.id}
											sx={{
												"& svg": {
													color:
														timeOption?.id === option?.id
															? "primary.main"
															: "black",
												},
											}}
										/>
										<Stack direction={"row"} alignItems={"center"} gap={0.5}>
											<Typography variant="subtitle2">{`${option.name}`}</Typography>
											<Typography
												variant="subtitle1"
												color={"primary.main"}
											>{`+${SET_PRICE_CURRENCY(
												option.addPrice.krw,
												language
											)}`}</Typography>
										</Stack>
									</Stack>
								))}
							</Stack>
						</>
					) : null}
					{/* ===== 종류형 옵션 ===== */}

					{/* ===== 종류형 옵션 ===== */}
					<Typography variant="subtitle2" color={"grey.100"}>
						{t("pickup-time")}
					</Typography>

					<Typography variant="subtitle2" color={"grey.100"}>
						{t("time")}
					</Typography>

					{/* ===== 시간대 ===== */}
					<Stack direction={"row"} gap={"15px"} flexWrap={"wrap"}>
						{!!timelineList.length &&
							timelineList?.map((timeline) => {
								return (
									<Button
										variant={
											activeTimeList === timeline ? "contained" : "outlined"
										}
										sx={{
											...selectButtonStyle(activeTimeList == timeline),
											py: 0.75,
										}}
										onClick={() => clickTimeLine(timeline)}
									>
										<Typography
											variant={
												activeTimeList === timeline ? "subtitle1" : "subtitle2"
											}
										>
											{timeline}
										</Typography>
									</Button>
								);
							})}
					</Stack>

					<Typography variant="subtitle2" color={"grey.100"}>
						{t("minute")}
					</Typography>

					{/* 시간 리스트====================== */}
					<Stack direction={"row"} gap={"15px"} flexWrap={"wrap"}>
						{!!timelist?.length &&
							timelist?.map((data) => {
								return (
									<Button
										key={data.idMenuTime}
										variant={
											time?.idMenuTime === data.idMenuTime ||
											!time?.active ||
											data?.stock === 0
												? "contained"
												: "outlined"
										}
										sx={{
											...selectButtonStyle(time?.idMenuTime == data.idMenuTime),
											flexDirection: "column",
											minWidth: 100,
											py: 1.5,
										}}
										onClick={() => {
											setCount(() => 1);
											setMaxCount(() => data.stock) & setTime(() => data);
										}}
										disabled={!time?.active || data?.stock === 0}
									>
										<Typography
											variant={
												time?.idMenuTime === data.idMenuTime ||
												!time?.active ||
												data?.stock === 0
													? "subtitle1"
													: "subtitle2"
											}
										>
											{dayjs(data.time).format("HH:mm")}
										</Typography>

										{menu?.maxQtyView && (
											<Typography
												variant="body2"
												color={
													time?.idMenuTime === data.idMenuTime ||
													!time?.active ||
													data?.stock === 0
														? "white"
														: "black"
												}
											>
												{data.stock === 0
													? t("sold-out")
													: t("unit", { count: data?.stock })}
											</Typography>
										)}
									</Button>
								);
							})}
					</Stack>
				</Stack>
			)}
		</>
	);
}
